import { useMemo, useState } from 'react';

import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';

import { ConfirmationDialog, useActionBarContribution, type ActionBarAction } from '~components';
import FormDialog from '~components/dialogs/formDialog';
import VectorImage from '~components/media/vectorImage';
import { pagePathSegments } from '~constants';
import { StatusVariant } from '~enums';
import { getService } from '~services/serviceFactory';

import EditOrganisationForm, { EditOrganisationFormResults } from '../forms/editOrganisationForm';
import OrganisationsService from '../services/organisationsService';

export type OrganisationActionBarActions = 'edit' | 'delete';

interface OrganisationActionBarProps {
	organisationId: string;
	showEditButton?: boolean;
	showDeleteButton?: boolean;
}

const organisationsService = getService(OrganisationsService);

const OrganisationActionBar = ({
	organisationId,
	showEditButton = true,
	showDeleteButton = true,
}: OrganisationActionBarProps) => {
	const { t } = useTranslation();
	const { mutate } = useSWRConfig();
	const navigate = useNavigate();

	const [userAction, setUserAction] = useState<OrganisationActionBarActions | null>(null);
	const [isUpdatingOrganisation, setIsUpdatingOrganisation] = useState<boolean>(false);

	const clearUserAction = () => {
		setUserAction(null);
	};

	const updateOrganisation = async (formData: EditOrganisationFormResults) => {
		setIsUpdatingOrganisation(true);
		await organisationsService
			.updateOrganisation(organisationId, formData)
			.then((updatedOrganisation) => {
				mutate(
					organisationsService.getOrganisationByIdCacheKey(organisationId),
					updatedOrganisation,
					false
				);
				enqueueSnackbar(
					t('successGeneric', {
						ns: 'toast',
						action: t('edited', { ns: 'status' }),
						value: t('organisation.labels.single', { ns: 'entities' }),
					}),
					{
						variant: 'success',
					}
				);
			})
			.catch(() => {
				enqueueSnackbar(
					t('errorGeneric', {
						ns: 'toast',
						action: t('edit', { ns: 'actions' }),
						value: t('organisation.labels.single', { ns: 'entities' }),
					}),
					{
						variant: 'error',
					}
				);
			})
			.finally(() => {
				clearUserAction();
				setIsUpdatingOrganisation(false);
			});
	};

	const deleteOrganisation = async () => {
		setIsUpdatingOrganisation(true);
		await organisationsService
			.deleteOrganisation(organisationId)
			.then(() => {
				enqueueSnackbar(
					t('successGeneric', {
						ns: 'toast',
						action: t('deleted', { ns: 'status' }),
						value: t('organisation.labels.single', { ns: 'entities' }),
					}),
					{ variant: 'success' }
				);
				navigate(`/${pagePathSegments.Organisations}`, { replace: true });
			})
			.catch(() => {
				enqueueSnackbar(
					t('errorGeneric', {
						ns: 'toast',
						action: t('delete', { ns: 'actions' }),
						value: t('organisation.labels.single', { ns: 'entities' }),
					}),
					{ variant: 'error' }
				);
			})
			.finally(() => {
				clearUserAction();
				setIsUpdatingOrganisation(false);
			});
	};

	const actions = useMemo<ActionBarAction[]>(() => {
		const list: ActionBarAction[] = [];

		if (showEditButton) {
			list.push({
				id: `organisation-${organisationId}-edit`,
				label: t('edit', { ns: 'actions' }),
				icon: <VectorImage name='icon/edit' />,
				onClick: () => setUserAction('edit'),
			});
		}

		if (showDeleteButton) {
			list.push({
				id: `organisation-${organisationId}-delete`,
				label: t('delete', { ns: 'actions' }),
				icon: <VectorImage name='icon/bin' />,
				variant: StatusVariant.Destructive,
				onClick: () => setUserAction('delete'),
			});
		}

		return list;
	}, [organisationId, showDeleteButton, showEditButton, t]);

	useActionBarContribution(actions, { id: `organisation-actions-${organisationId}` });

	return (
		<>
			<ConfirmationDialog
				open={userAction === 'delete'}
				title={t('organisation.fields.deleteOrganisationDialogTitle', { ns: 'entities' })}
				helperText={t('organisation.fields.deleteOrganisationDialogDescription', {
					ns: 'entities',
				})}
				icon={<VectorImage name='icon/bin' />}
				confirmButtonText={t('delete', { ns: 'actions' })}
				onConfirm={deleteOrganisation}
				onClose={clearUserAction}
				variant={StatusVariant.Destructive}
			/>
			<FormDialog
				open={userAction === 'edit'}
				title={
					t('edit', { ns: 'actions' }) +
					' ' +
					t('organisation.labels.single', { ns: 'entities' }).toLocaleLowerCase()
				}
				loading={isUpdatingOrganisation}
			>
				<EditOrganisationForm
					organisationId={organisationId}
					onSubmit={async (formData) => updateOrganisation(formData)}
					onCancel={clearUserAction}
				/>
			</FormDialog>
		</>
	);
};

export default OrganisationActionBar;
